var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed, makeObservable } from 'mobx';
import { QuestionScoreFactor, QuestionTypeName } from '../../../learnware/LearnwareEnums';
import { runtime } from '../../../runtime/Runtime';
import { sortedSet } from '../../../../utils/mobx/decorators/customComputed';
import { head, round } from '../../../../utils/helper/lodash_replacements';
/*
Magintudes:
- 1 000 000 000 : Question is out!
+ 1 000 000 : Prefer this question!
+ 1 000 000 000 : Take this question in any case!

 */
export class MiniQuestionMixedScore {
    parent;
    constructor(parent) {
        makeObservable(this);
        this.parent = parent;
    }
    get miniQuestion() {
        return this.parent;
    }
    get app() {
        return this.miniQuestion.app;
    }
    get ownPosedAnswers() {
        return this.app.tray.trayAnswers.answersPosed.filter(a => a.question_id === this.miniQuestion.id);
    }
    get ownPosedAnswer() {
        return head(this.ownPosedAnswers);
    }
    get deviationOfAverage() {
        return round(this.ownPosedAnswer ? this.ownPosedAnswer.deviationOfAverage : -0.3, 4);
    }
    get minutesSinceLastAnswer() {
        if (!this.ownPosedAnswer || !this.ownPosedAnswer.completeDate)
            return 9999999;
        const diff = this.app.appClock.clock_minute.tickingTime - this.ownPosedAnswer.completeDate.getTime();
        return diff / 60000;
    }
    get ownNthAnswer() {
        return this.ownPosedAnswers.length + 1;
    }
    get history() {
        return this.app.history.historyAnswers;
    }
    /* Scoring */
    get scoreTotal() {
        // ALWAYS ROUND NUMBERS!
        /* console.log(
          this
        ); */
        if (!this.miniQuestion.micro)
            return -99999;
        const totalScore = Math.round(this.scoreLeastOftenShown +
            this.scoreDidacticalRepetition +
            this.scoreVocabularyRepetition +
            this.scoreIsPreviousQuestion +
            this.scoreIsIn10PreviousQuestions +
            this.scoreAdaptiveDifficulty +
            this.scoreQuestionIsInNativeTopic +
            this.scoreProgressiveDifficulty +
            this.scoreAlterQuestionType +
            this.scoreAlterSubtopic +
            this.scoreAlterLayout +
            this.scoreTouchFriendlyQuestionType +
            this.scoreSkippedRate +
            this.scoreKeepStageType +
            this.scorePunishOldLayouts +
            this.scoreExitRate +
            this.scoreLevel);
        return totalScore;
    }
    get scoreLeastOftenShown() {
        return Math.round(-(((this.ownNthAnswer - 1) * QuestionScoreFactor.LeastOftenShown) / 3));
    }
    get scoreDidacticalRepetition() {
        const m = this.minutesSinceLastAnswer;
        if (m < 10)
            return -QuestionScoreFactor.DidacticalRepetition * 5;
        if (m > 20 && m < 30 && this.ownNthAnswer === 2) {
            return Math.round(QuestionScoreFactor.DidacticalRepetition * 0.5 * (-this.deviationOfAverage * 10));
        }
        if (m > 60 * 12)
            return Math.round(QuestionScoreFactor.DidacticalRepetition * (-this.deviationOfAverage * 10));
        return 0;
    }
    get scoreVocabularyRepetition() {
        if (this.miniQuestion.questionType !== QuestionTypeName.Vocabulary)
            return 0;
        const m = this.minutesSinceLastAnswer;
        if (m > 30)
            return -300;
        if (m > 10)
            return 900;
        if (m > 7)
            return 600;
        if (m > 3)
            return 300;
        return -500;
    }
    get scoreIsPreviousQuestion() {
        if (!this.app.history.historyRecentAnswers.latestFinishedAnswerSincePageload)
            return 0;
        return this.app.history.historyRecentAnswers.latestFinishedAnswerSincePageload.question_id === this.miniQuestion.id ? -99999 : 0;
    }
    get scoreIsIn10PreviousQuestions() {
        if (this.app.questionId === this.miniQuestion.id) {
            // not current question again
            return -99999;
        }
        return this.app.history.historyAnswers.answersFinished
            .concat()
            .reverse()
            .slice(0, 10)
            .map(a => a.question_id)
            .includes(this.miniQuestion.id)
            ? -9999
            : 0;
    }
    get scoreFromQuestionRating() {
        if (this.miniQuestion.micro.rating < 0.45) {
            // punish badly rated questions
            return (1 - this.miniQuestion.micro.rating) * -5 * QuestionScoreFactor.BestRated;
        }
        return (this.miniQuestion.micro.rating - 0.5) * 5 * QuestionScoreFactor.BestRated;
    }
    get scoreAdaptiveDifficulty() {
        return 0;
        // QuestionScoreFactor.AdaptiveDifficulty
        // todo reached from current grade
        // return Math.abs((1-(avg from current grade)) - this.miniQuestion.average) * 150;
    }
    get scoreQuestionIsInNativeTopic() {
        return this.miniQuestion.topic.id === this.app.solidTopic.id ? QuestionScoreFactor.IsNativeInTopic : 0;
    }
    get scoreProgressiveDifficulty() {
        const scoreGain = (this.app.tray.trayAnswers.answersCompleted.length / 100) *
            this.miniQuestion.micro.difficulty *
            QuestionScoreFactor.ProgressiveDifficulty;
        const maxScoreGain = QuestionScoreFactor.ProgressiveDifficulty * 2;
        return Math.round(Math.min(scoreGain, maxScoreGain));
    }
    get scoreAlterQuestionType() {
        const { lastCompletedAnswer } = this.app.tray.trayAnswers;
        return lastCompletedAnswer &&
            lastCompletedAnswer.miniQuestion &&
            lastCompletedAnswer.miniQuestion.questionType !== this.miniQuestion.questionType
            ? QuestionScoreFactor.AlterQuestionType
            : 0;
    }
    get scoreAlterSubtopic() {
        const { lastCompletedAnswer } = this.app.tray.trayAnswers;
        return lastCompletedAnswer &&
            lastCompletedAnswer.miniQuestion &&
            lastCompletedAnswer.miniQuestion.subtopicName !== this.miniQuestion.subtopicName
            ? QuestionScoreFactor.AlterSubtopic
            : 0;
    }
    get scoreAlterLayout() {
        const changeLayoutInRound = 0 -
            this.app.tray.trayAnswers.answersPosedSincePageLoad
                .map(answer => answer.miniQuestion)
                .filter(q => !!q && q.micro && q.micro.layout === this.miniQuestion.micro.layout).length *
                QuestionScoreFactor.AlterLayout;
        const numberOfSameLayoutsInLast2Answers = this.app.tray.trayAnswers.answersPosedSincePageLoad
            .slice(-2)
            .map(answer => answer.miniQuestion)
            .filter(q => !!q && q.micro && q.micro.layout === this.miniQuestion.micro.layout).length;
        const alterLayoutsInSuccession = 0 - numberOfSameLayoutsInLast2Answers * QuestionScoreFactor.AlterLayout;
        return changeLayoutInRound + alterLayoutsInSuccession;
    }
    get scorePunishOldLayouts() {
        if (this.miniQuestion.micro.layoutName.includes('Abfrager')) {
            return -500;
        }
        if (['H73nDbTXW', 'HE4Lanoar'].includes(this.miniQuestion.micro.layout) && // Hunderterfeld
            (runtime.solidBrowserRuntime.browserAgent.isOldEdge || runtime.solidBrowserRuntime.browserDevice.isTabletOrSmartphone)) {
            return -100000;
        }
        return 0;
    }
    get scoreKeepStageType() {
        if (!this.app.tray.trayAnswers.lastCompletedAnswer)
            return 0;
        if (!this.app.tray.trayAnswers.lastCompletedAnswer.miniQuestion)
            return 0;
        if (!this.app.tray.trayAnswers.lastCompletedAnswer.miniQuestion.micro)
            return 0;
        if (this.app.tray.trayAnswers.lastCompletedAnswer.miniQuestion.micro.layoutName.includes('Abfrager') ===
            this.miniQuestion.micro.layoutName.includes('Abfrager')) {
            return 1000;
        }
        return -10000;
    }
    get scoreTouchFriendlyQuestionType() {
        if (runtime.isBrowserRuntime && runtime.solidBrowserRuntime.browserDevice.isTouchDevice) {
            if ([QuestionTypeName.MultipleChoice, QuestionTypeName.CorrectWrong, QuestionTypeName.ThinkAndReveal, QuestionTypeName.OrderedChoice, QuestionTypeName.MultiSelect].includes(this.miniQuestion.questionType)) {
                return QuestionScoreFactor.TouchFriendlyQuestionType * 30;
            }
            if ([QuestionTypeName.Associate].includes(this.miniQuestion.questionType)) {
                return QuestionScoreFactor.TouchFriendlyQuestionType * 20;
            }
        }
        return 0;
    }
    get scoreSkippedRate() {
        return Math.round(-1 * this.miniQuestion.micro.skipRate * QuestionScoreFactor.SkipRate);
    }
    get scoreExitRate() {
        return Math.round(-1 * this.miniQuestion.micro.exitRate * QuestionScoreFactor.ExitRate);
    }
    get scoreLevel() {
        const foundLower = this.app.tray.funnel.funnelIncoming
            .allMiniQuestionsBelowLevel(this.miniQuestion.micro.level)
            .find(mini => mini.micro.levelTag === this.miniQuestion.micro.levelTag);
        if (foundLower)
            return Math.round(-5 * QuestionScoreFactor.LevelSuccession);
        return 0;
    }
}
__decorate([
    sortedSet
], MiniQuestionMixedScore.prototype, "ownPosedAnswers", null);
__decorate([
    computed
], MiniQuestionMixedScore.prototype, "scoreTotal", null);
__decorate([
    computed
], MiniQuestionMixedScore.prototype, "scoreProgressiveDifficulty", null);
